import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, BACKEND_URL } from '@/App';
import {
  Shield, Sword, Flame, Users, Trophy, Calendar as CalendarIcon, ChevronDown,
  MapPin, Zap, MessageCircle, ArrowRight, Sparkles, Instagram
} from 'lucide-react';

/* ============================================================================
   MALLORCA KLAN LANDING – público (sin login)
   URL: /mallorcaklan
   ========================================================================= */

const WHATSAPP_NUMBER = '34678592508';
const WHATSAPP_TEXT = encodeURIComponent(
  'Hola! Vi la app de Mallorca Klan y me interesa unirme. ¿Puedo probar una clase?'
);
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_TEXT}`;

const HOW_STEPS = [
  { n: 1, title: 'Contacta', desc: 'Escríbenos por WhatsApp y te contamos horarios y sitios.', icon: MessageCircle },
  { n: 2, title: 'Prueba una clase gratis', desc: 'Ven a un entreno cualquiera. Sin compromiso.', icon: Sparkles },
  { n: 3, title: 'Únete al Klan', desc: 'Alta en la app y acceso al calendario, retos, hall of fame...', icon: Shield },
  { n: 4, title: 'Entrena y crece', desc: 'Marca tus récords, sigue tus estadísticas y forma parte de la comunidad.', icon: Flame },
];

const APP_FEATURES = [
  { t: 'Calendario de preparación', d: 'Planifica tus entrenos semana a semana y sigue tu progreso hacia carreras y retos personales.', icon: CalendarIcon },
  { t: 'Retos del Klan', d: 'Podios olímpicos, rankings por sexo y récords de la comunidad. La Bici Matadora te espera.', icon: Trophy },
  { t: 'Muro del Klan', d: 'Feed en vivo de todos los entrenos, récords, check-ins y logros del Klan. Nunca entrenas solo.', icon: Users },
  { t: 'Hall of Fame', d: 'Los mejores tiempos y marcas de cada disciplina. Aparece en el ranking y demuestra de qué estás hecho.', icon: Zap },
];

const FAQS = [
  {
    q: '¿Necesito estar en forma para empezar?',
    a: 'No. El Klan lo formamos personas de todos los niveles. Lo importante es la actitud, no el punto de partida. Todos empezamos alguna vez.',
  },
  {
    q: '¿Hay clases mixtas o por niveles?',
    a: 'Son clases mixtas donde en la mayoría de las veces se adaptan al nivel de cada uno. Trabajamos juntos pero cada uno a su ritmo.',
  },
  {
    q: '¿Dónde entrenamos?',
    a: 'En varias localizaciones de Marratxí. Rotamos según la sesión: parques, pistas, senderos… La variedad forma parte del método.',
  },
  {
    q: '¿Puedo probar una clase antes?',
    a: '¡Claro que sí! La primera clase es gratis y sin compromiso. Escríbenos por WhatsApp y te decimos el próximo entreno al que puedes venir.',
  },
];

const MANIFIESTO = [
  { line: 'No somos un gimnasio.' },
  { line: 'Somos un Klan.' },
  { line: 'Entrenamos con disciplina espartana, corazón nórdico y espíritu de manada.' },
  { line: 'Aquí nadie entrena solo. Aquí cada récord se celebra y cada caída se levanta juntos.' },
];

/* -------------------------------------------------------------------------- */
/*   COMPONENTES                                                              */
/* -------------------------------------------------------------------------- */

function Hero({ heroPhoto, onCta }) {
  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden" data-testid="landing-hero">
      {/* Foto de fondo */}
      <div className="absolute inset-0">
        {heroPhoto ? (
          <img
            src={heroPhoto}
            alt="Mallorca Klan"
            className="w-full h-full object-cover"
            style={{ filter: 'saturate(0.9) contrast(1.1) brightness(0.55)' }}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-black via-neutral-900 to-black" />
        )}
        {/* Overlay dorado + oscuro */}
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at center, rgba(245,158,11,0.15) 0%, transparent 50%), linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0.95) 100%)'
        }} />
        {/* Grain */}
        <div className="absolute inset-0 opacity-25 mix-blend-overlay" style={{
          backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>")',
        }} />
      </div>

      {/* Contenido */}
      <div className="relative z-10 text-center px-6 max-w-3xl">
        <div className="inline-flex items-center gap-2 px-3 py-1 mb-6 border border-gold/40 rounded-full bg-black/50 backdrop-blur-sm">
          <Shield className="w-3.5 h-3.5 text-gold" />
          <span className="text-[10px] uppercase tracking-[0.3em] text-gold font-bold">Comunidad · Marratxí</span>
        </div>

        <h1
          className="font-black text-white leading-[0.85] mb-6"
          style={{
            fontSize: 'clamp(3rem, 12vw, 8rem)',
            letterSpacing: '0.02em',
            textShadow: '0 4px 30px rgba(0,0,0,0.9), 0 0 40px rgba(245,158,11,0.25)',
          }}
        >
          MALLORCA
          <br />
          <span
            style={{
              background: 'linear-gradient(180deg, #fde047 0%, #f59e0b 60%, #b45309 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              filter: 'drop-shadow(0 4px 20px rgba(245,158,11,0.4))',
            }}
          >
            KLAN
          </span>
        </h1>

        <p className="text-lg sm:text-xl text-gray-200 max-w-xl mx-auto mb-10 font-medium" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8)' }}>
          Entrena como un guerrero, vive como un Klan.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={onCta}
            className="group px-8 py-4 bg-gradient-to-r from-yellow-400 via-gold to-yellow-600 text-black font-black uppercase tracking-widest text-sm rounded-full shadow-2xl shadow-yellow-500/40 hover:shadow-yellow-500/70 transition-all hover:scale-105 flex items-center gap-2"
            data-testid="hero-cta-btn"
          >
            Únete al Klan
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
          <a
            href="#manifiesto"
            className="px-6 py-3 text-gray-300 hover:text-gold text-sm uppercase tracking-widest transition-colors flex items-center gap-1"
          >
            Descubre más
            <ChevronDown className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* Scroll hint */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
        <ChevronDown className="w-6 h-6 text-gold/70" />
      </div>
    </section>
  );
}

function Manifiesto() {
  return (
    <section id="manifiesto" className="relative py-24 sm:py-32 px-6 bg-black" data-testid="landing-manifiesto">
      <div className="absolute inset-0 opacity-40 pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(245,158,11,0.15), transparent 40%), radial-gradient(circle at 80% 70%, rgba(245,158,11,0.08), transparent 40%)',
      }} />
      <div className="relative max-w-3xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 mb-6">
          <div className="h-px w-8 bg-gold/50" />
          <Sword className="w-4 h-4 text-gold" />
          <span className="text-[10px] uppercase tracking-[0.4em] text-gold font-bold">Manifiesto</span>
          <Sword className="w-4 h-4 text-gold scale-x-[-1]" />
          <div className="h-px w-8 bg-gold/50" />
        </div>
        <div className="space-y-4">
          {MANIFIESTO.map((m, i) => (
            <p
              key={i}
              className="text-white font-bold leading-tight"
              style={{
                fontSize: i < 2 ? 'clamp(1.75rem, 5vw, 3rem)' : 'clamp(1rem, 2.5vw, 1.5rem)',
                color: i < 2 ? '#fff' : '#d1d5db',
                textShadow: i < 2 ? '0 0 20px rgba(245,158,11,0.15)' : 'none',
              }}
            >
              {i < 2 ? (
                <span style={{
                  background: 'linear-gradient(180deg, #fff 0%, #d1d5db 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  {m.line}
                </span>
              ) : m.line}
            </p>
          ))}
        </div>
      </div>
    </section>
  );
}

function AppScreenshots() {
  // Capturas de la app: usamos rutas relativas del propio dominio (deben existir en /app/frontend/public)
  // Alternativa: usar imágenes con mock/svg. Aquí uso "mock cards" que replican el look de la app.
  const cards = [
    {
      title: 'Calendario semanal',
      desc: 'Todos tus entrenos, en un solo sitio. Fácil de leer, imposible de olvidar.',
      color: '#22d3ee',
      icon: CalendarIcon,
    },
    {
      title: 'Retos del Klan',
      desc: 'Podios olímpicos y ranking en vivo. Compite contra tus compañeros y contra ti mismo.',
      color: '#fbbf24',
      icon: Trophy,
    },
    {
      title: 'Hall of Fame',
      desc: 'Los mejores tiempos y marcas. Aparece en las tablas y deja tu huella en el Klan.',
      color: '#f472b6',
      icon: Zap,
    },
    {
      title: 'Muro del Klan',
      desc: 'Feed en vivo con lo que hace la comunidad. Reacciones, comentarios y logros.',
      color: '#34d399',
      icon: Users,
    },
  ];

  return (
    <section className="relative py-24 px-6 bg-neutral-950" data-testid="landing-app-features">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="h-px w-8 bg-gold/50" />
            <span className="text-[10px] uppercase tracking-[0.4em] text-gold font-bold">La app</span>
            <div className="h-px w-8 bg-gold/50" />
          </div>
          <h2 className="text-3xl sm:text-5xl font-black text-white mb-3">
            Todo el Klan, <span className="text-gold">en tu bolsillo</span>
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto">
            Una app diseñada para el Klan y por el Klan. Sin banners, sin publicidad, sin ruido. Solo lo que necesitas para entrenar mejor.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <div
                key={c.title}
                className="group relative rounded-2xl border border-gray-800 bg-gradient-to-br from-neutral-900 to-black p-6 sm:p-7 hover:border-gray-700 transition-all overflow-hidden"
                style={{ borderTopColor: c.color, borderTopWidth: 2 }}
                data-testid={`app-feature-${c.title.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full opacity-10 blur-2xl transition-opacity group-hover:opacity-25" style={{ background: c.color }} />
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: `${c.color}22`, boxShadow: `0 0 20px ${c.color}22` }}
                >
                  <Icon className="w-5 h-5" style={{ color: c.color }} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{c.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{c.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="relative py-24 px-6 bg-black" data-testid="landing-how">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="h-px w-8 bg-gold/50" />
            <span className="text-[10px] uppercase tracking-[0.4em] text-gold font-bold">Cómo unirte</span>
            <div className="h-px w-8 bg-gold/50" />
          </div>
          <h2 className="text-3xl sm:text-5xl font-black text-white">
            Del primer <span className="text-gold">click</span> al primer entreno
          </h2>
        </div>

        <div className="relative">
          {/* Línea vertical dorada */}
          <div className="absolute left-6 sm:left-1/2 top-4 bottom-4 w-px bg-gradient-to-b from-transparent via-gold/40 to-transparent -translate-x-1/2 hidden sm:block" />

          <div className="space-y-8 sm:space-y-14">
            {HOW_STEPS.map((s, i) => {
              const Icon = s.icon;
              const isRight = i % 2 === 0;
              return (
                <div
                  key={s.n}
                  className={`flex items-center gap-4 sm:gap-6 ${isRight ? 'sm:flex-row' : 'sm:flex-row-reverse'}`}
                  data-testid={`how-step-${s.n}`}
                >
                  <div className={`flex-1 ${isRight ? 'sm:text-right' : 'sm:text-left'} text-left`}>
                    <div className={`inline-flex items-center gap-2 mb-1 ${isRight ? 'sm:flex-row-reverse' : ''}`}>
                      <span className="text-4xl sm:text-5xl font-black text-gold/30 leading-none">0{s.n}</span>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-white">{s.title}</h3>
                    <p className="text-sm text-gray-400 mt-1 max-w-sm">{s.desc}</p>
                  </div>
                  <div className="relative flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-700 flex items-center justify-center shadow-[0_0_25px_rgba(245,158,11,0.35)]">
                      <Icon className="w-5 h-5 text-black" strokeWidth={2.5} />
                    </div>
                  </div>
                  <div className="flex-1 hidden sm:block" />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function PhotoGallery({ photos }) {
  if (!photos || photos.length === 0) return null;
  return (
    <section className="relative py-24 px-6 bg-neutral-950" data-testid="landing-gallery">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="h-px w-8 bg-gold/50" />
            <span className="text-[10px] uppercase tracking-[0.4em] text-gold font-bold">Momentos del Klan</span>
            <div className="h-px w-8 bg-gold/50" />
          </div>
          <h2 className="text-3xl sm:text-5xl font-black text-white">
            Esto es <span className="text-gold">el Klan</span>
          </h2>
        </div>

        {/* Grid asimétrico */}
        <div className="grid grid-cols-4 grid-rows-2 gap-2 sm:gap-3 auto-rows-[100px] sm:auto-rows-[180px]">
          {photos.slice(0, 6).map((p, i) => {
            // Pattern: 1 grande, 2 pequeños, 3 medianos
            const layouts = [
              'col-span-2 row-span-2',
              'col-span-2 row-span-1',
              'col-span-1 row-span-1',
              'col-span-1 row-span-1',
              'col-span-1 row-span-1',
              'col-span-1 row-span-1',
            ];
            return (
              <div
                key={p.id}
                className={`relative overflow-hidden rounded-lg group ${layouts[i] || 'col-span-1 row-span-1'}`}
                data-testid={`gallery-photo-${i}`}
              >
                <img
                  src={p.url?.startsWith('http') ? p.url : `${BACKEND_URL}${p.url}`}
                  alt={p.titulo || `Momento ${i + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section className="relative py-24 px-6 bg-black" data-testid="landing-faq">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="h-px w-8 bg-gold/50" />
            <span className="text-[10px] uppercase tracking-[0.4em] text-gold font-bold">Dudas frecuentes</span>
            <div className="h-px w-8 bg-gold/50" />
          </div>
          <h2 className="text-3xl sm:text-5xl font-black text-white">
            Lo que <span className="text-gold">todo el mundo</span> pregunta
          </h2>
        </div>

        <div className="space-y-2">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div
                key={i}
                className={`rounded-xl border transition-all ${isOpen ? 'border-gold/40 bg-gold/5' : 'border-gray-800 bg-neutral-900/50 hover:border-gray-700'}`}
                data-testid={`faq-item-${i}`}
              >
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className={`font-bold text-sm sm:text-base ${isOpen ? 'text-gold' : 'text-white'}`}>{f.q}</span>
                  <ChevronDown className={`w-5 h-5 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180 text-gold' : 'text-gray-500'}`} />
                </button>
                {isOpen && (
                  <div className="px-5 pb-4 text-gray-300 text-sm leading-relaxed border-t border-gold/20 pt-3">
                    {f.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CtaFinal({ onCta }) {
  return (
    <section className="relative py-24 px-6 overflow-hidden" data-testid="landing-cta-final">
      {/* Fondo dorado con textura */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-500 via-amber-600 to-yellow-800" />
        <div className="absolute inset-0 opacity-20 mix-blend-overlay" style={{
          backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>")',
        }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-black/20" />
      </div>

      <div className="relative max-w-3xl mx-auto text-center">
        <Shield className="w-14 h-14 text-black mx-auto mb-6 drop-shadow-[0_4px_12px_rgba(0,0,0,0.3)]" strokeWidth={2} />
        <h2 className="text-4xl sm:text-6xl font-black text-black mb-4 leading-tight" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.15)' }}>
          ¿Listo para unirte al Klan?
        </h2>
        <p className="text-black/80 text-lg sm:text-xl mb-10 font-medium max-w-xl mx-auto">
          Una sola clase. Sin compromiso. Descubre qué se siente al entrenar en manada.
        </p>

        <button
          onClick={onCta}
          className="group px-10 py-5 bg-black text-gold font-black uppercase tracking-widest text-base rounded-full shadow-2xl hover:bg-neutral-900 transition-all hover:scale-105 inline-flex items-center gap-3"
          data-testid="cta-final-btn"
        >
          <MessageCircle className="w-5 h-5" />
          Contactar por WhatsApp
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </button>

        <p className="text-black/70 text-xs mt-6 uppercase tracking-widest font-bold">
          O escríbenos al 678 59 25 08
        </p>
      </div>
    </section>
  );
}

function Footer() {
  const navigate = useNavigate();
  return (
    <footer className="bg-black border-t border-gray-900 py-10 px-6 text-center" data-testid="landing-footer">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-center gap-2 mb-4">
          <Shield className="w-5 h-5 text-gold" />
          <span className="text-white font-black tracking-widest">MALLORCA KLAN</span>
        </div>
        <p className="text-gray-500 text-xs mb-6 max-w-md mx-auto">
          Comunidad deportiva en Marratxí. Entrenamos con cabeza, corazón y manada.
        </p>
        <div className="flex items-center justify-center gap-4">
          <a
            href="https://instagram.com/mallorcaklan"
            target="_blank"
            rel="noreferrer"
            className="text-gray-500 hover:text-gold transition-colors"
            aria-label="Instagram"
          >
            <Instagram className="w-5 h-5" />
          </a>
          <button
            onClick={() => navigate('/login')}
            className="text-gray-500 hover:text-gold text-xs uppercase tracking-widest transition-colors"
            data-testid="landing-login-btn"
          >
            Ya soy del Klan · Login
          </button>
        </div>
        <p className="text-gray-700 text-[10px] mt-6">© {new Date().getFullYear()} Mallorca Klan</p>
      </div>
    </footer>
  );
}

/* -------------------------------------------------------------------------- */
/*   PÁGINA PRINCIPAL                                                          */
/* -------------------------------------------------------------------------- */

export default function MallorcaKlanLanding() {
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    axios.get(`${API}/photos`).then((res) => {
      const list = Array.isArray(res.data) ? res.data : [];
      // Elegir las 20 primeras (más recientes por defecto) y barajar para variedad
      const shuffled = [...list].sort(() => Math.random() - 0.5).slice(0, 12);
      setPhotos(shuffled);
    }).catch(() => setPhotos([]));

    // Meta tags para compartir (Instagram, WhatsApp preview)
    document.title = 'Mallorca Klan · Entrena como un guerrero, vive como un Klan';
  }, []);

  const heroPhoto = photos.length > 0
    ? (photos[0].url?.startsWith('http') ? photos[0].url : `${BACKEND_URL}${photos[0].url}`)
    : null;

  const handleCta = () => {
    window.open(WHATSAPP_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="bg-black text-white min-h-screen" data-testid="mallorcaklan-landing">
      <Hero heroPhoto={heroPhoto} onCta={handleCta} />
      <Manifiesto />
      <AppScreenshots />
      <HowItWorks />
      <PhotoGallery photos={photos.slice(1)} />
      <FAQ />
      <CtaFinal onCta={handleCta} />
      <Footer />
    </div>
  );
}
